// The end markers of a condition have nothing to type inside. Left editable,
// they swallowed the caret placed right before them: a space typed between a
// key and {{Fin Si}} landed inside the marker and vanished at generation.
const END_MARKERS = ['{{END-IF}}', '{{ELSE}}'];

// The opener stays editable: its key is picked or typed straight inside it.
const OPENER_SELECTOR = '[data-template-key="{{IF}}"]';

// Shape briefly given to openers, a non-editable chip around an editable
// slot: the caret could not leave the slot once in it. It is undone.
const SLOT_SELECTOR = '[data-template-slot="condition"]';
const SLOT_PLACEHOLDER = '...';

const setEditable = (span, editable) => {
  const value = editable ? 'true' : 'false';
  if (span.getAttribute('contenteditable') === value) return false;
  span.setAttribute('contenteditable', value);
  return true;
};

/**
 * Brings an opener back to one editable chip. A slot gives way to what it
 * held, or to the placeholder text when it held nothing.
 * @function
 *
 * @param {HTMLElement} chip - The opener span.
 * @returns {boolean} True when the chip was changed.
 */
const restoreOpener = (chip) => {
  const madeEditable = setEditable(chip, true);
  const slot = chip.querySelector(SLOT_SELECTOR);
  if (!slot) return madeEditable;

  const text = (slot.textContent || '').trim();
  const holdsSomething =
    slot.querySelector('*') || (text && text !== SLOT_PLACEHOLDER);
  if (holdsSomething) {
    while (slot.firstChild) slot.parentNode.insertBefore(slot.firstChild, slot);
  } else {
    slot.parentNode.insertBefore(
      chip.ownerDocument.createTextNode(SLOT_PLACEHOLDER),
      slot
    );
  }
  slot.remove();
  return true;
};

/**
 * Brings the condition chips of a stored template to their current shape:
 * end markers non-editable, opener editable with its key directly inside.
 * Markup that needs no change is returned untouched.
 * @function
 *
 * @param {string} html - The template fragment.
 * @returns {string} The fragment with normalised condition chips.
 */
export const normalizeConditionMarkers = (html) => {
  if (typeof html !== 'string' || !html.trim()) return html;
  if (typeof DOMParser === 'undefined') return html;

  const document = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;

  END_MARKERS.forEach((marker) => {
    document.body
      .querySelectorAll(`[data-template-key="${marker}"]`)
      .forEach((span) => {
        if (setEditable(span, false)) changed = true;
      });
  });

  document.body.querySelectorAll(OPENER_SELECTOR).forEach((chip) => {
    if (restoreOpener(chip)) changed = true;
  });

  return changed ? document.body.innerHTML : html;
};
